import React, { useEffect } from 'react';
import { formatTime } from './utils';
import { FiClock, FiUser, FiMapPin, FiAlignLeft, FiLink, FiArrowRight } from 'react-icons/fi';

const MeetingTooltip = ({ meeting, x, top, bottom, onClose, onNavigate }) => {
  if (!meeting) return null;

  // Make sure it stays within window bounds roughly
  const safeX = Math.min(Math.max(x, 150), window.innerWidth - 150);
  
  // Decide whether to render below or above
  const isBottomHalf = bottom > window.innerHeight / 2;
  
  const verticalStyle = isBottomHalf 
    ? { bottom: `${window.innerHeight - top + 10}px` } 
    : { top: `${bottom + 10}px` };

  return (
    <>
      <div className="fixed inset-0 z-[9998]" onClick={onClose} />
      <div 
        className="fixed z-[9999] bg-white border border-slate-200 shadow-2xl rounded-xl p-4 w-72 transform -translate-x-1/2 cursor-auto"
        style={{ left: `${safeX}px`, ...verticalStyle }}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className={`w-2.5 h-2.5 rounded-full shadow-sm shrink-0 ${meeting.status === 'COMPLETED' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
            <h4 className="font-bold text-slate-800 text-sm truncate">{meeting.title}</h4>
          </div>
        </div>
        
        <div className="flex flex-col gap-2 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <FiClock className="text-blue-500 shrink-0" size={14} />
            <span className="font-medium">{new Date(meeting.meetingStart).toLocaleDateString()} • {formatTime(meeting.meetingStart)} - {formatTime(meeting.meetingEnd)}</span>
          </div>
          
          {meeting.organizerName && (
            <div className="flex items-center gap-2">
              <FiUser className="text-emerald-500 shrink-0" size={14} />
              <span className="truncate font-medium">{meeting.organizerName}</span>
            </div>
          )}
          
          {meeting.location && (
            <div className="flex items-center gap-2">
              <FiMapPin className="text-amber-500 shrink-0" size={14} />
              <span className="truncate">{meeting.location}</span>
            </div>
          )}
          
          {meeting.meetingLink && (
            <div className="flex items-center gap-2">
              <FiLink className="text-indigo-500 shrink-0" size={14} />
              <a 
                href={meeting.meetingLink.startsWith('http') ? meeting.meetingLink : `https://${meeting.meetingLink}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="truncate text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
              >
                Join Meeting
              </a>
            </div>
          )}
          
          {meeting.description && (
            <div className="mt-2 pt-3 border-t border-slate-100 flex items-start gap-2">
              <FiAlignLeft className="text-slate-400 shrink-0 mt-0.5" size={14} />
              <p className="line-clamp-3 text-slate-500 leading-relaxed italic">{meeting.description}</p>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
            <button 
              onClick={() => onNavigate(meeting.id)}
              className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-semibold text-xs transition-colors"
            >
              View Details <FiArrowRight />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MeetingTooltip;
